import { NumberTicker } from "@/components/ui/number-ticker";

export interface StatsRowProps {
  items: Array<{
    number: string;
    label: string;
    description?: string;
  }>;
  variant?: "stats" | "steps";
  dark?: boolean;
  className?: string;
  animateNumbers?: boolean;
}

/**
 * Parse a stat string like "$100M+", "500+", "4.9" into parts for NumberTicker.
 * Returns null if the string can't be parsed into a numeric value.
 */
function parseStatNumber(raw: string): { value: number; prefix: string; suffix: string; decimals: number } | null {
  const match = raw.match(/^([^0-9]*)([0-9]+(?:\.[0-9]+)?)(.*)$/);
  if (!match) return null;
  const [, prefix, numStr, suffix] = match;
  const value = parseFloat(numStr);
  if (isNaN(value)) return null;
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return { value, prefix, suffix, decimals };
}

function AnimatedStat({ raw, className, style }: { raw: string; className?: string; style?: React.CSSProperties }) {
  const parsed = parseStatNumber(raw);
  if (!parsed) return <span className={className} style={style}>{raw}</span>;
  return (
    <span className={className} style={style}>
      <NumberTicker value={parsed.value} prefix={parsed.prefix} suffix={parsed.suffix} decimalPlaces={parsed.decimals} delay={0.2} />
    </span>
  );
}

export function StatsRow({
  items,
  variant = "stats",
  dark = false,
  className = "",
  animateNumbers = false,
}: StatsRowProps) {
  const c = dark
    ? {
        bg: "var(--charcoal)",
        text: "var(--cream)",
        textMuted: "hsla(48, 24%, 88%, 0.72)",
        border: "var(--cream)",
        terra: "var(--terra-lt)",
        dotColor: "rgba(232, 100, 58, 0.24)",
      }
    : {
        bg: "var(--cream)",
        text: "var(--charcoal)",
        textMuted: "var(--charcoal)",
        border: "var(--charcoal)",
        terra: "var(--terra)",
        dotColor: "var(--dot-terra)",
      };

  return (
    <section
      className={`relative ${className}`}
      style={{ backgroundColor: c.bg }}
    >
      {/* Full-width top border — the grid IS visible */}
      <div style={{ borderTop: `2.5px solid ${c.border}` }} />

      {/* Full-width grid — no container, borders edge-to-edge */}
      <div
        className="grid grid-cols-2 lg:grid-cols-4"
        style={{ borderBottom: `2.5px solid ${c.border}` }}
      >
        {items.map((item, i) => {
          const isStats = variant === "stats";
          const stepNum = item.number || String(i + 1).padStart(2, "0");

          return (
            <div
              key={i}
              className={`group relative ${
                /* In 2-col mode, odd-indexed items (right column) should NOT have right border */
                i % 2 === 0 ? "border-r-[2.5px]" : "border-r-0 lg:border-r-[2.5px]"
              } ${
                /* Last item in 4-col never gets right border */
                i === items.length - 1 ? "!border-r-0" : ""
              } ${
                /* In 2-col mode, first row gets bottom border */
                i < 2 ? "border-b-[2.5px] lg:border-b-0" : ""
              }`}
              style={{
                borderColor: c.border,
                // Terra dot-matrix in each cell
                backgroundImage: `radial-gradient(circle, ${c.dotColor} 1px, transparent 1px)`,
                backgroundSize: "14px 14px",
              }}
            >
              {/* Content — centered in cell */}
              <div className="px-4 sm:px-6 md:px-8 lg:px-10 py-8 sm:py-10 md:py-12 lg:py-14 text-center">
                {isStats ? (
                  <>
                    <div
                      className="type-stat"
                      style={{ color: c.text }}
                    >
                      {animateNumbers ? <AnimatedStat raw={item.number} /> : item.number}
                    </div>
                    <div
                      className="type-label"
                      style={{
                        color: c.textMuted,
                        marginTop: 16,
                      }}
                    >
                      {item.label}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="type-stat"
                      style={{
                        color: c.terra,
                      }}
                    >
                      {stepNum}
                    </div>
                    <h3
                      className="type-h3"
                      style={{ color: c.text, marginTop: 16 }}
                    >
                      {item.label}
                    </h3>
                    {item.description && (
                      <p
                        className="type-body"
                        style={{
                          color: c.textMuted,
                          marginTop: 10,
                        }}
                      >
                        {item.description}
                      </p>
                    )}
                  </>
                )}
              </div>

            </div>
          );
        })}
      </div>
    </section>
  );
}
